import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import app from './firebase';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import './index.css';
// Import email validation
import { validateBusinessEmail } from './utils/emailValidation';

// Protected route component
const ProtectedRoute = ({ children, user }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔍 Auth state changed. User:', user?.email);
      
      if (user) {
        // CRITICAL: Additional validation at app level
        console.log('🔐 Validating user email at app level:', user.email);
        
        // Check if email exists
        if (!user.email) {
          console.log('❌ No email found, signing out user');
          await signOut(auth);
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Validate business email
        const emailValidation = validateBusinessEmail(user.email);
        if (!emailValidation.isValid) {
          console.log('❌ BLOCKING USER AT APP LEVEL:', user.email, emailValidation.message);
          
          // Immediately sign out the user
          try {
            await signOut(auth);
            console.log('✅ Successfully signed out personal email user');
          } catch (error) {
            console.error('Error signing out user:', error);
          }
          
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Additional Gmail check
        const emailLower = user.email.toLowerCase();
        if (emailLower.includes('@gmail.') || emailLower.includes('@googlemail.')) {
          console.log('❌ BLOCKING GMAIL USER AT APP LEVEL:', user.email);
          
          try {
            await signOut(auth);
            console.log('✅ Successfully signed out Gmail user');
          } catch (error) {
            console.error('Error signing out Gmail user:', error);
          }
          
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Personal domains check
        const personalDomains = [
          'gmail.com', 'googlemail.com', 'gmail.co.uk', 'googlemail.co.uk',
          'hotmail.com', 'outlook.com', 'live.com', 'msn.com',
          'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'rocketmail.com',
          'aol.com', 'aim.com', 'icloud.com', 'me.com', 'mac.com',
          'protonmail.com', 'tutanota.com'
        ];
        
        const domain = emailLower.split('@')[1];
        if (personalDomains.includes(domain)) {
          console.log('❌ BLOCKING PERSONAL DOMAIN AT APP LEVEL:', domain);
          
          try {
            await signOut(auth);
            console.log('✅ Successfully signed out personal domain user');
          } catch (error) {
            console.error('Error signing out personal domain user:', error);
          }
          
          setUser(null);
          setLoading(false);
          return;
        }
        
        console.log('✅ Business email validated at app level:', user.email);
        setUser(user);
      } else {
        console.log('🚪 No user signed in');
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/" element={
          <ProtectedRoute user={user}>
            <Dashboard user={user} />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App; 